// Storage adapter for the Optimize workspace. Reads/writes the SAME
// `consistency_optimized_variants` localStorage array the legacy
// `OptimizeContent.jsx` already writes and `Scheduler.jsx` already reads
// (`id`, `assetId`, `creatorId`, `platforms[]`, `title`, `description`,
// `hashtags`, `status`). Nothing here renames or removes any of those
// fields — Scheduler.jsx needs zero changes.
//
// Section 11 ("independent platform variants") is implemented as an
// ADAPTER, not a schema break: picking N platforms for one piece of
// content creates N sibling records (one platform each, same shape
// Scheduler already expects from `platforms`), tied together by a new,
// additive `groupId` field Scheduler simply never looks at.
const VARIANTS_KEY = 'consistency_optimized_variants';
const BRAND_KEYWORDS_KEY = (creatorId) => `consistency_brand_keywords_${creatorId}`;

const newId = () => `variant_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

function readJson(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback)); } catch { return fallback; }
}

export function getAllVariants() {
  return readJson(VARIANTS_KEY, []);
}

export function saveAllVariants(list) {
  localStorage.setItem(VARIANTS_KEY, JSON.stringify(list));
}

/** Every existing variant record for one content item, oldest first. New records (this task) all carry `contentKind`; legacy records from the old OptimizeContent.jsx will not, and are still matched by `assetId` alone. */
export function getVariantsForContent(assetId) {
  return getAllVariants().filter((v) => v.assetId === assetId);
}

/** Build a brand-new, schema-compatible variant record for one (content, platform) pair. */
export function buildVariantRecord({ contentRef, platform, groupId }) {
  return {
    // Legacy/Scheduler-required fields:
    id: newId(),
    assetId: contentRef.id,
    creatorId: contentRef.creatorId,
    platforms: [platform],
    title: '',
    description: '',
    hashtags: '',
    status: 'draft', // only 'ready' is ever picked up by Scheduler.jsx's readyVariants filter
    thumbnailLF: null,
    thumbnailSF: null,
    brandingApplied: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    // New, additive fields (ignored by Scheduler.jsx / old OptimizeContent.jsx):
    groupId,
    projectId: contentRef.projectId || null,
    contentKind: contentRef.kind,
    contentTitle: contentRef.title || '',
    cta: '',
    keywords: { brand: [], platform: [], content: [] },
    thumbnail: { source: 'none', url: null },
    brandkitId: null,
    reviewStatus: 'draft', // draft | pending | approved | rejected — richer than Scheduler's binary status
    score: null,
  };
}

export function upsertVariant(variant) {
  const all = getAllVariants();
  const idx = all.findIndex((v) => v.id === variant.id);
  const next = { ...variant, updatedAt: new Date().toISOString() };
  if (idx === -1) all.push(next); else all[idx] = next;
  saveAllVariants(all);
  return next;
}

export function removeVariant(variantId) {
  saveAllVariants(getAllVariants().filter((v) => v.id !== variantId));
}

/** Map a richer `reviewStatus` onto the exact `status` value Scheduler.jsx's readyVariants filter expects — 'ready' only on approval, so nothing new is silently schedulable. */
export function statusForReview(reviewStatus) {
  return reviewStatus === 'approved' ? 'ready' : 'draft';
}

// ── Brand Keywords (creator-level, genuinely persisted — see section 6/7 notes in OptimizeWorkspace.jsx: PublishingMetadataDemo.jsx itself saves nothing today, so this is the one real, creator-scoped keyword list available to reuse across every piece of content). ──
export function getBrandKeywords(creatorId) {
  if (!creatorId) return [];
  return readJson(BRAND_KEYWORDS_KEY(creatorId), []);
}
export function saveBrandKeywords(creatorId, keywords) {
  if (!creatorId) return;
  localStorage.setItem(BRAND_KEYWORDS_KEY(creatorId), JSON.stringify(Array.from(new Set(keywords.map((k) => k.trim()).filter(Boolean)))));
}

// ── Search volume / competition (section 10) ──
// No connected keyword-research data source exists anywhere in this
// codebase today (checked: no search_volume/competition/trends provider
// in the backend). Every lookup below returns `available:false` so the
// UI can show "Data Unavailable" honestly instead of fabricating a
// number. The shape (`fetchedAt`, `ttlMinutes`) is deliberately ready for
// a future real provider + cache to fill in without a UI rewrite.
export function getSearchVolumeInfo(_keyword, _platform) {
  return { available: false, searchVolume: null, competition: null, fetchedAt: null, ttlMinutes: 60, reason: 'No connected keyword-research data source yet.' };
}
